import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UserPlus, Trash, UsersThree } from "@phosphor-icons/react";

const inputCls = "w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm focus:border-red-600 focus:outline-none";

export default function Roster() {
  const [artists, setArtists] = useState([]);
  const [form, setForm] = useState({ display_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/label/artists").then(({ data }) => setArtists(data.artists)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/label/artists", form);
      toast.success(`${form.display_name} added to your roster`);
      setForm({ display_name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not add artist"); }
    finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.display_name} from your roster? Their splits on existing releases will be cleared.`)) return;
    await api.delete(`/label/artists/${a.id}`);
    toast.success("Removed"); load();
  };

  return (
    <section className="mb-14" data-testid="roster-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl uppercase tracking-tight">Roster</h2>
          <p className="text-zinc-500 text-xs mt-1">Sub-artists under your label. Assign royalty splits per release at upload — they see their share on their own dashboard.</p>
        </div>
        <span className="text-xs text-zinc-500">{artists.length} artist{artists.length === 1 ? "" : "s"}</span>
      </div>
      <div className="grid lg:grid-cols-12 border border-zinc-800">
        <div className="lg:col-span-7 border-r border-zinc-800">
          {artists.length === 0 ? (
            <div className="p-10 text-center text-zinc-600 text-xs flex flex-col items-center gap-3"><UsersThree size={28} weight="duotone" className="text-red-600" />No artists on the roster yet.</div>
          ) : artists.map(a => (
            <div key={a.id} className="flex items-center gap-4 px-6 py-4 border-b border-zinc-800 last:border-b-0 row-hover text-sm" data-testid={`roster-artist-${a.id}`}>
              <div className="w-9 h-9 bg-zinc-900 border border-zinc-800 flex items-center justify-center font-display text-red-500">{a.display_name.slice(0, 1).toUpperCase()}</div>
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{a.display_name}</div><div className="text-[10px] text-zinc-500 truncate">{a.email}</div></div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500">{a.release_count} release{a.release_count === 1 ? "" : "s"}</div>
              <button onClick={() => remove(a)} data-testid={`roster-remove-${a.id}`} className="text-zinc-600 hover:text-red-500"><Trash size={14} /></button>
            </div>
          ))}
        </div>
        <form onSubmit={add} className="lg:col-span-5 p-6 space-y-3">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Add Artist</div>
          <input required placeholder="Artist name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="roster-name" className={inputCls} />
          <input required type="email" placeholder="artist@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="roster-email" className={inputCls} />
          <input required type="password" minLength={8} placeholder="Login password (8+ chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="roster-password" className={inputCls} />
          <button disabled={busy} data-testid="roster-submit" className="w-full bg-red-600 text-white py-3 text-[10px] tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 btn-brutal btn-brutal-red disabled:opacity-50">
            <UserPlus size={14} weight="bold" /> {busy ? "Adding..." : "Add to Roster"}
          </button>
          <p className="text-[10px] text-zinc-600 leading-relaxed">They log in with these credentials and see only the releases and royalty share you assign them.</p>
        </form>
      </div>
    </section>
  );
}
